import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import {
  GameSubTypeForm,
  GameTypeForm,
  emptyGameSubTypeForm,
  emptyGameTypeForm,
  type GameSubTypeFormValue,
  type GameTypeFormValue,
} from '../components/gameForms'
import type { GameType } from './GameTypes'
import type { GameSubType } from './GameSubTypes'

const TABS = ['summary', 'subtypes'] as const
type TabValue = (typeof TABS)[number]

/**
 * GameTypeDetail is one game type and everything hanging off it, split into
 * tabs: its own fields (id, name) and its subtypes.
 *
 * HasGameHubStructure / HasSportsbookHubStructure are deliberately not shown:
 * they are platform wiring, not something an operator sets from here. They are
 * still carried in the edit payload untouched, so a save cannot clear them.
 *
 * The active tab lives in the URL, so the list page links its subtype count
 * straight to the relation and a reload keeps you there.
 *
 * The id is never editable here — GameHub.Game and Platform.GameSubType both
 * carry it as a foreign key, so changing it would silently re-label their rows.
 * And retiring a type does not touch its subtypes: that is why they are listed
 * with their own status, so an operator sees exactly what is left running.
 */
export function GameTypeDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<GameTypeFormValue>(emptyGameTypeForm)
  const [subDialogOpen, setSubDialogOpen] = useState(false)
  const [editingSub, setEditingSub] = useState<GameSubType | null>(null)
  const [subForm, setSubForm] = useState<GameSubTypeFormValue>(emptyGameSubTypeForm)

  const { data: gameType } = useQuery({
    queryKey: ['game-types', id],
    queryFn: async (): Promise<GameType> => {
      const res = await api.get(`/game-types/${id}`)
      return res.data as GameType
    },
  })

  const { data: subData } = useQuery({
    queryKey: ['game-subtypes', { gameTypeId: id, all: true }],
    queryFn: async (): Promise<{ gameSubTypes: GameSubType[] }> => {
      const res = await api.get('/game-subtypes', {
        params: { gameTypeId: id, includeDisabled: 'true', limit: 200 },
      })
      return res.data as { gameSubTypes: GameSubType[] }
    },
  })

  // Seed the edit form from the loaded type (and re-seed after a save).
  useEffect(() => {
    if (!gameType) return
    setForm({
      gameTypeId: String(gameType.gameTypeId),
      gameType: gameType.gameType,
      hasGameHubStructure: gameType.hasGameHubStructure,
      hasSportsbookHubStructure: gameType.hasSportsbookHubStructure,
    })
  }, [gameType])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['game-types'] })
    qc.invalidateQueries({ queryKey: ['game-subtypes'] })
  }
  const onError = (err: unknown) => setError(messageOf(err, t('gameTypes.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/game-types/${id}`, {
        gameType: form.gameType.trim(),
        hasGameHubStructure: form.hasGameHubStructure,
        hasSportsbookHubStructure: form.hasSportsbookHubStructure,
        isDisabledByPlatform: gameType?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      gameType?.isDisabledByPlatform
        ? api.post(`/game-types/${id}/restore`)
        : api.delete(`/game-types/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const saveSub = useMutation({
    mutationFn: () => {
      const body = {
        gameTypeId: Number(id),
        gameSubType: subForm.gameSubType.trim(),
        icon: subForm.icon.trim(),
        isDisabledByPlatform: editingSub?.isDisabledByPlatform ?? false,
      }
      return editingSub
        ? api.put(`/game-subtypes/${editingSub.gameSubTypeId}`, body)
        : api.post('/game-subtypes', body)
    },
    onSuccess: () => {
      setSubDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameSubTypes.errors.save'))),
  })

  const toggleSub = useMutation({
    mutationFn: (g: GameSubType) =>
      g.isDisabledByPlatform
        ? api.post(`/game-subtypes/${g.gameSubTypeId}/restore`)
        : api.delete(`/game-subtypes/${g.gameSubTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameSubTypes.errors.save'))),
  })

  const subTypes = subData?.gameSubTypes ?? []

  const openCreateSub = () => {
    setEditingSub(null)
    setSubForm({ ...emptyGameSubTypeForm, gameTypeId: String(id ?? '') })
    setError('')
    setSubDialogOpen(true)
  }

  const openEditSub = (g: GameSubType) => {
    setEditingSub(g)
    setSubForm({ gameTypeId: String(g.gameTypeId), gameSubType: g.gameSubType, icon: g.icon })
    setError('')
    setSubDialogOpen(true)
  }

  if (!gameType) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

  return (
    <div>
      <Link
        to="/game-types"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('gameTypes.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{gameType.gameType}</h2>
          <p className="text-sm text-muted-foreground">
            {t('gameTypes.columns.id')} {gameType.gameTypeId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={gameType.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {gameType.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {gameType.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('gameTypes.tabs.summary')}</Tab>
          <Tab value="subtypes">
            {t('gameTypes.tabs.subtypes')}
            <TabCount value={subTypes.length} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <GameTypeForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              isCreate={false}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[12rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <SummaryRow label={t('gameTypes.columns.id')}>
                      <span className="font-mono text-xs">{gameType.gameTypeId}</span>
                    </SummaryRow>
                    <SummaryRow label={t('gameTypes.columns.name')}>{gameType.gameType}</SummaryRow>
                    <SummaryRow label={t('gameTypes.columns.subTypes')}>
                      {gameType.subTypeCount}
                    </SummaryRow>
                    <SummaryRow label={t('common.columns.created')}>
                      {formatDate(gameType.creationDate)}
                    </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(gameType.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- subtypes ---- */}
        <TabsPanel value="subtypes">
          {canWrite && (
            <div className="mb-3 flex justify-end">
              <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
                <DialogTrigger
                  render={
                    <Button size="sm" onClick={openCreateSub}>
                      <Plus />
                      {t('gameSubTypes.newGameSubType')}
                    </Button>
                  }
                />
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingSub
                        ? t('gameSubTypes.editGameSubType')
                        : t('gameSubTypes.newGameSubType')}
                    </DialogTitle>
                  </DialogHeader>
                  {/* The parent is this page, so the picker is hidden rather
                      than offering a move that belongs on the subtypes page. */}
                  <GameSubTypeForm
                    value={subForm}
                    onChange={setSubForm}
                    onSubmit={() => saveSub.mutate()}
                    onCancel={() => setSubDialogOpen(false)}
                    gameTypes={[gameType]}
                    lockParent
                    pending={saveSub.isPending}
                    error={error}
                    submitLabel={editingSub ? t('common.save') : t('common.create')}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('gameSubTypes.columns.id')}</TableHead>
                  <TableHead>{t('gameSubTypes.columns.name')}</TableHead>
                  <TableHead className="w-64">{t('gameSubTypes.columns.icon')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {subTypes.map((g) => (
                  <TableRow key={g.gameSubTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {g.gameSubTypeId}
                    </TableCell>
                    <TableCell className="font-medium">{g.gameSubType}</TableCell>
                    <TableCell className="truncate font-mono text-xs text-muted-foreground">
                      {g.icon || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={g.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEditSub(g)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={g.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggleSub.isPending}
                            onClick={() => toggleSub.mutate(g)}
                          >
                            {g.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {subTypes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('gameSubTypes.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
